import { Box, ButtonBase, Stack, Typography, useTheme } from "@mui/material";
import { MooneIcon } from "Components/Shared/Icons/MooneIcon";
import { useNavigate } from "Navigation/useNavigate";
import { appUrl } from "Utils/UrlUtils";
import { useResource } from "Translations/Resources";
import { Resources } from "Translations/Resources";
import type { StatusVariant } from "Components/Chat/transactionStatusUtils";

const PAYMENT_REQUEST_RESOURCES = Resources.Chat.PaymentRequest;
const PAYMENT_MENU_RESOURCES = Resources.Chat.PaymentMenu;

type Props = {
  publicID: string;
  variant: StatusVariant;
  isAwaitingPayment: boolean;
  isCurrentUserCreditor: boolean;
  canBePaid: boolean;
  /** The viewer's Lite tier keeps them from paying this request (App ADR-0013). */
  isPayRestricted?: boolean;
};

const SHARED_STYLES = {
  borderRadius: 3,
  mx: 1,
  mb: 1,
  py: 1.5,
  px: 2.5,
} as const;

export const PaymentRequestStatusBar = ({
  publicID,
  variant,
  isAwaitingPayment,
  isCurrentUserCreditor,
  canBePaid,
  isPayRestricted = false,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useResource();
  const theme = useTheme();

  const icon = (
    <Box
      width={25}
      height={25}
      borderRadius="50%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ backgroundColor: "rgba(255,255,255,0.3)" }}
    >
      {canBePaid ? (
        <MooneIcon sx={{ color: "common.white", fontSize: 20 }} />
      ) : (
        variant.icon
      )}
    </Box>
  );

  const resolveLabel = () => {
    if (!isAwaitingPayment) {
      return variant.labelKey(t);
    }
    if (isPayRestricted) {
      return t(PAYMENT_MENU_RESOURCES.LitePayUnavailable);
    }
    return t(
      isCurrentUserCreditor
        ? PAYMENT_REQUEST_RESOURCES.Pending
        : PAYMENT_REQUEST_RESOURCES.Pay,
    );
  };

  const label = (
    <Typography fontWeight={600} fontSize={18} color={variant.statusColor}>
      {resolveLabel()}
    </Typography>
  );

  if (canBePaid) {
    return (
      <ButtonBase
        onClick={() =>
          navigate(`${appUrl("customer-payment-pay")}/${publicID}`)
        }
        sx={{
          ...SHARED_STYLES,
          width: "100%",
          backgroundColor: theme.palette.primary.main,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
        }}
      >
        {icon}
        {label}
      </ButtonBase>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      gap={1.5}
      sx={{ ...SHARED_STYLES, backgroundColor: variant.statusBackground }}
    >
      {icon}
      {label}
    </Stack>
  );
};
